import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { PasswordField } from '@/components/PasswordField';
import { authErrorKey } from '@/features/auth/authErrors';
import { signIn } from '@/features/auth/useSession';

export default function SignInPage() {
  const { t } = useTranslation(['auth', 'errors', 'common']);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorKey(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate('/today', { replace: true });
    } catch (error) {
      // A mensagem sai do código que o GoTrue devolveu, não de um genérico.
      // A primeira versão desta tela mostrava "Entre na sua conta para
      // continuar" para qualquer falha — numa tela de login, isso não informa
      // nada, e deixou uma conta legítima sem caminho de volta.
      setErrorKey(authErrorKey(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />
      <h1 className="text-3xl">{t('auth:signIn.title')}</h1>
      <p className="mt-1 mb-8 text-(--color-sand-700)">{t('auth:signIn.subtitle')}</p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="mh-field">
          <label htmlFor="email">{t('auth:signIn.email')}</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <PasswordField
          id="password"
          label={t('auth:signIn.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />

        {errorKey ? (
          <Alert tone="error" live="assertive">
            {t(errorKey)}
          </Alert>
        ) : null}

        <button type="submit" className="mh-button mh-button--primary mt-2" disabled={busy}>
          {busy ? t('common:state.loading') : t('auth:signIn.submit')}
        </button>
      </form>

      <p className="mt-5 text-center">
        <Link to="/forgot-password" className="text-sm font-medium text-(--color-amber-700)">
          {t('auth:signIn.forgot')}
        </Link>
      </p>

      <p className="mt-8 text-center text-sm text-(--color-sand-700)">
        {t('auth:signIn.noAccount')}{' '}
        <Link to="/sign-up" className="font-semibold text-(--color-amber-700)">
          {t('auth:signIn.signUpLink')}
        </Link>
      </p>
    </main>
  );
}
